import { type ReactNode } from "react";
import { Link } from "wouter";
import {
  CalendarCheck,
  Clock,
  FileSignature,
  Gavel,
  LayoutDashboard,
  Link2,
  LogOut,
  Mail,
  Mic,
  Paperclip,
  Scale,
  Search,
} from "lucide-react";

import { supabase } from "@/lib/supabase";
import { markIntentionalSignout } from "@/lib/intentionalSignout";

/**
 * AvocatShell — cadre de navigation du Cockpit Avocat (vision 2.0, lot Shell).
 *
 * PRAGMATIQUE : sidebar dédiée + badges de comptage. MODULAIRE : emplacements
 * réservés (placeholders) pour Timeline 3D et Assistant vocal. Séparation
 * logique/vues : ce composant ne fait QUE la mise en page.
 */
const MARINE = "oklch(0.115 0.038 243)";
const GOLD = "oklch(0.695 0.105 68)";
const BORDER = "color-mix(in oklch, oklch(0.695 0.105 68) 18%, transparent)";

export interface AvocatNavCounts {
  conclusions?: number;
  pieces?: number;
  audiences?: number;
  decisions?: number;
  client?: number;
  correspondance?: number;
}

type NavId = keyof AvocatNavCounts | "overview";

const NAV: { id: NavId; label: string; icon: ReactNode }[] = [
  { id: "overview", label: "Vue d'ensemble", icon: <LayoutDashboard size={15} /> },
  { id: "conclusions", label: "Conclusions", icon: <FileSignature size={15} /> },
  { id: "pieces", label: "Pièces", icon: <Paperclip size={15} /> },
  { id: "audiences", label: "Audiences", icon: <CalendarCheck size={15} /> },
  { id: "decisions", label: "Décisions", icon: <Gavel size={15} /> },
  { id: "client", label: "Client", icon: <Link2 size={15} /> },
  { id: "correspondance", label: "Correspondance", icon: <Mail size={15} /> },
];

function countFor(
  counts: AvocatNavCounts | undefined,
  id: NavId,
): number | undefined {
  if (!counts || id === "overview") return undefined;
  return counts[id];
}

function CountBadge({ n }: { n?: number }) {
  if (!n) return null;
  return (
    <span
      className="text-[10px] font-bold rounded-full px-1.5 py-0.5"
      style={{ background: GOLD, color: MARINE }}
    >
      {n}
    </span>
  );
}

function PlaceholderItem({ icon, label }: { icon: ReactNode; label: string }) {
  return (
    <div
      className="flex items-center gap-2 rounded-md px-2 py-1.5 text-sm"
      style={{ color: "oklch(0.7 0.01 243)", opacity: 0.6 }}
      title="Fonctionnalité à venir (Cockpit Avocat 2.0)"
    >
      <span>{icon}</span>
      <span className="flex-1">{label}</span>
      <span className="text-[9px] uppercase">bientôt</span>
    </div>
  );
}

export function AvocatShell({
  children,
  counts,
}: {
  children: ReactNode;
  counts?: AvocatNavCounts;
}) {
  return (
    <div
      className="min-h-screen"
      style={{ background: MARINE, color: "oklch(1 0 0)" }}
    >
      <header
        className="px-4 sm:px-6 py-4 border-b flex items-center gap-3"
        style={{ borderColor: BORDER }}
      >
        <Scale size={20} style={{ color: GOLD }} />
        <div>
          <div className="font-semibold">Cockpit Avocat</div>
          <div
            className="text-xs"
            style={{ color: GOLD, letterSpacing: "0.12em" }}
          >
            GSF ARBITRAL AI
          </div>
          <div
            className="mt-0.5 text-[10px] font-bold tracking-widest"
            style={{ color: GOLD, letterSpacing: "0.18em", opacity: 0.85 }}
          >
            AVOCAT · CONSEIL
          </div>
        </div>
        <Link
          href="/avocat/recherche"
          className="ml-auto inline-flex items-center gap-1 text-xs underline"
          style={{ color: GOLD }}
        >
          <Search size={12} /> Recherche IA
        </Link>
        <Link
          href="/formation"
          className="ml-3 text-xs underline"
          style={{ color: GOLD }}
        >
          Formation
        </Link>
        <Link
          href="/profil"
          className="ml-3 text-xs underline"
          style={{ color: GOLD }}
        >
          Mon profil
        </Link>
        <Link
          href="/dashboard"
          className="ml-3 text-xs underline"
          style={{ color: GOLD }}
        >
          Tableau de bord général
        </Link>
        <button
          type="button"
          onClick={() => {
            // Signout volontaire : marquer AVANT l'appel async, sinon
            // l'AuthContext déclenche le magic link auto-recovery (cf. ArbiterShell).
            markIntentionalSignout();
            void supabase.auth.signOut();
          }}
          className="ml-3 inline-flex items-center gap-1 text-xs underline"
          style={{ color: GOLD }}
        >
          <LogOut size={12} /> Déconnexion
        </button>
      </header>

      <div className="flex">
        <aside
          className="hidden lg:flex flex-col gap-1 w-56 shrink-0 border-r p-3"
          style={{ borderColor: BORDER }}
        >
          {NAV.map((item) => (
            <a
              key={item.id}
              href={`#${item.id}`}
              className="flex items-center gap-2 rounded-md px-2 py-1.5 text-sm"
              style={{ color: "oklch(0.9 0.01 243)" }}
            >
              <span style={{ color: GOLD }}>{item.icon}</span>
              <span className="flex-1">{item.label}</span>
              <CountBadge n={countFor(counts, item.id)} />
            </a>
          ))}

          <div
            className="mt-4 pt-3 border-t space-y-1"
            style={{ borderColor: BORDER }}
          >
            <div
              className="px-2 text-[10px] uppercase tracking-widest"
              style={{ color: GOLD, opacity: 0.7 }}
            >
              À venir (2.0)
            </div>
            <PlaceholderItem icon={<Clock size={15} />} label="Timeline 3D" />
            <PlaceholderItem icon={<Mic size={15} />} label="Assistant vocal" />
          </div>
        </aside>

        <main className="flex-1 min-w-0">
          <nav
            className="lg:hidden flex gap-2 overflow-x-auto px-4 py-2 border-b text-xs"
            style={{ borderColor: BORDER }}
          >
            {NAV.map((item) => (
              <a
                key={item.id}
                href={`#${item.id}`}
                className="whitespace-nowrap rounded-md px-2 py-1"
                style={{ color: GOLD, border: `1px solid ${BORDER}` }}
              >
                {item.label}
              </a>
            ))}
          </nav>
          <div className="px-4 sm:px-6 py-6 max-w-4xl mx-auto w-full space-y-8">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
}
